import client from '../helpers/directusClient';
import { readItems } from '@directus/sdk';

class PermissionsManager {
  constructor() {
    this.permissions = null;
    this.lastFetch = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Get user role from localStorage or context
  getCurrentUserRole() {
    const userRole = localStorage.getItem('userRole');
    const userData = localStorage.getItem('user');
    
    if (userRole) return userRole;
    if (userData) {
      const user = JSON.parse(userData);
      return user.role;
    }
    return null;
  }

  // Get company ID from localStorage
  getCompanyId() {
    const companyData = localStorage.getItem('company');
    if (companyData) {
      const company = JSON.parse(companyData);
      return company.id;
    }
    return null;
  }

  // Fetch permissions from backend or localStorage
  async fetchPermissions() {
    const companyId = this.getCompanyId();
    if (!companyId) return null;

    // Check if we have cached permissions
    if (this.permissions && this.lastFetch && 
        (Date.now() - this.lastFetch) < this.cacheTimeout) {
      return this.permissions;
    }

    try {
      // Try to fetch from backend first
      const response = await client.request(
        readItems('role_permissions', {
          fields: ['*'],
          filter: {
            company_id: { _eq: companyId }
          }
        })
      );

      if (response && response.length > 0) {
        const permissions = {};
        response.forEach(item => {
          permissions[item.role_key] = JSON.parse(item.permissions);
        });
        
        this.permissions = permissions;
        this.lastFetch = Date.now();
        return permissions;
      }
    } catch (error) {
      console.log('Backend permissions not available, using localStorage fallback');
    }

    // Fallback to localStorage
    const localPermissions = localStorage.getItem(`rolePermissions_${companyId}`);
    if (localPermissions) {
      this.permissions = JSON.parse(localPermissions);
      this.lastFetch = Date.now();
      return this.permissions;
    }

    // Default permissions if nothing is found
    const defaultPermissions = {
      admin: {
        view_reports: true,
        manage_users: true,
        manage_loans: true,
        view_financial_data: true,
        manage_branches: true,
        manage_loan_products: true,
        view_borrower_profiles: true,
        manage_collateral: true
      },
      loan_officer: {
        view_reports: false,
        manage_users: false,
        manage_loans: true,
        view_financial_data: false,
        manage_branches: false,
        manage_loan_products: false,
        view_borrower_profiles: true,
        manage_collateral: true
      },
      branch_manager: {
        view_reports: true,
        manage_users: false,
        manage_loans: true,
        view_financial_data: true,
        manage_branches: false,
        manage_loan_products: true,
        view_borrower_profiles: true,
        manage_collateral: true
      },
      credit_manager: {
        view_reports: true,
        manage_users: false,
        manage_loans: true,
        view_financial_data: true,
        manage_branches: false,
        manage_loan_products: false,
        view_borrower_profiles: true,
        manage_collateral: true
      },
      rider: {
        view_reports: false,
        manage_users: false,
        manage_loans: false,
        view_financial_data: false,
        manage_branches: false,
        manage_loan_products: false,
        view_borrower_profiles: false,
        manage_collateral: false,
        track_riders: true,
        view_rider_tracking: true
      }
    };

    this.permissions = defaultPermissions;
    this.lastFetch = Date.now();
    return defaultPermissions;
  }

  // Check if current user has a specific permission
  async hasPermission(permissionKey) {
    const userRole = this.getCurrentUserRole();
    if (!userRole) return false;

    // Admin always has all permissions
    if (userRole === 'admin') return true;

    const permissions = await this.fetchPermissions();
    if (!permissions || !permissions[userRole]) return false;

    return permissions[userRole][permissionKey] || false;
  }

  // Check multiple permissions at once
  async hasPermissions(permissionKeys) {
    const results = {};
    for (const key of permissionKeys) {
      results[key] = await this.hasPermission(key);
    }
    return results;
  }

  // Get all permissions for current user
  async getUserPermissions() {
    const userRole = this.getCurrentUserRole();
    if (!userRole) return {};

    const permissions = await this.fetchPermissions();
    return permissions?.[userRole] || {};
  }

  // Clear cache - useful when permissions are updated
  clearCache() {
    this.permissions = null;
    this.lastFetch = null;
  }

  // Convenience methods for common permission checks
  async canViewReports() {
    return await this.hasPermission('view_reports');
  }

  async canManageUsers() {
    return await this.hasPermission('manage_users');
  }

  async canManageLoans() {
    return await this.hasPermission('manage_loans');
  }

  async canViewFinancialData() {
    return await this.hasPermission('view_financial_data');
  }

  async canManageBranches() {
    return await this.hasPermission('manage_branches');
  }

  async canManageLoanProducts() {
    return await this.hasPermission('manage_loan_products');
  }

  async canViewBorrowerProfiles() {
    return await this.hasPermission('view_borrower_profiles');
  }

  async canManageCollateral() {
    return await this.hasPermission('manage_collateral');
  }

  async canTrackRiders() {
    return await this.hasPermission('track_riders');
  }

  async canViewRiderTracking() {
    return await this.hasPermission('view_rider_tracking');
  }
}

// Create a singleton instance
const permissionsManager = new PermissionsManager();

// Export convenience functions
export const hasPermission = (permissionKey) => permissionsManager.hasPermission(permissionKey);
export const hasPermissions = (permissionKeys) => permissionsManager.hasPermissions(permissionKeys);
export const getUserPermissions = () => permissionsManager.getUserPermissions();
export const clearPermissionsCache = () => permissionsManager.clearCache();

// Export convenience permission checks
export const canViewReports = () => permissionsManager.canViewReports();
export const canManageUsers = () => permissionsManager.canManageUsers();
export const canManageLoans = () => permissionsManager.canManageLoans();
export const canViewFinancialData = () => permissionsManager.canViewFinancialData();
export const canManageBranches = () => permissionsManager.canManageBranches();
export const canManageLoanProducts = () => permissionsManager.canManageLoanProducts();
export const canViewBorrowerProfiles = () => permissionsManager.canViewBorrowerProfiles();
export const canManageCollateral = () => permissionsManager.canManageCollateral();
export const canTrackRiders = () => permissionsManager.canTrackRiders();
export const canViewRiderTracking = () => permissionsManager.canViewRiderTracking();

export default permissionsManager;